import React, { createContext, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';
import { Home } from 'lucide-react';

// Breadcrumb Context for sharing record data
interface BreadcrumbContextData {
  resourceId?: string;
  resourceName?: string;
  resourceType?: string;
}

interface BreadcrumbContextValue {
  data: BreadcrumbContextData;
  setData: (data: BreadcrumbContextData) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  data: {},
  setData: () => {},
});

export const useBreadcrumbContext = () => useContext(BreadcrumbContext);

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<BreadcrumbContextData>({});

  return (
    <BreadcrumbContext.Provider value={{ data, setData }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export function AppBreadcrumb() {
  const location = useLocation();
  const pathname = location.pathname;
  const { data: contextData } = useBreadcrumbContext();

  // Generate breadcrumb items based on current route
  const generateBreadcrumbs = () => {
    const crumbs = [];

    // Always start with Dashboard/Home
    crumbs.push({
      label: 'Dashboard',
      href: '/',
      isHome: true
    });

    // Handle different route patterns
    if (pathname === '/') {
      return crumbs;
    }

    // Split path and analyze segments
    const segments = pathname.split('/').filter(Boolean);

    if (segments.length > 0) {
      const resource = segments[0];
      const resourceId = segments[1];
      const action = segments[2];

      // Map resource names to display names
      const resourceLabels: Record<string, string> = {
        'users': 'Users',
        'locations': 'Locations',
        'reviews': 'Match Reviews',
        'session-reviews': 'Session Reviews',
        'notifications': 'Notifications',
        'notification-logs': 'Notification Logs',
        'device-tokens': 'Device Tokens',
        'settings': 'Settings',
        'sessions': 'Sessions',
        'bookings': 'Bookings',
        'trainers': 'Trainers',
        'trainer-applications': 'Trainer Applications',
        'session-chats': 'Session Chats',
      };

      // Add resource breadcrumb
      if (resourceLabels[resource]) {
        crumbs.push({
          label: resourceLabels[resource],
          href: `/${resource}`,
        });

        // Handle specific item routes (show, edit)
        if (resourceId && !isNaN(Number(resourceId))) {
          // Use context data for meaningful names if available
          const itemLabel = contextData.resourceName || `#${resourceId}`;

          // If we have an ID, we're viewing/editing a specific item
          if (action === 'show') {
            crumbs.push({
              label: itemLabel,
              href: `/${resource}/${resourceId}/show`,
            });
          } else if (action === 'edit') {
            crumbs.push({
              label: itemLabel,
              href: `/${resource}/${resourceId}/show`,
            });
            crumbs.push({
              label: 'Edit',
              href: `/${resource}/${resourceId}/edit`,
            });
          }
        } else if (resourceId === 'create') {
          // Handle create routes
          crumbs.push({
            label: 'Create',
            href: `/${resource}/create`,
          });
        } else if (resourceId && action) {
          // Handle other specific actions
          crumbs.push({
            label: resourceId.charAt(0).toUpperCase() + resourceId.slice(1),
            href: `/${resource}/${resourceId}`,
          });
          if (action !== 'show') {
            crumbs.push({
              label: action.charAt(0).toUpperCase() + action.slice(1),
              href: `/${resource}/${resourceId}/${action}`,
            });
          }
        }
      }
    }

    return crumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  
  // Always show breadcrumbs consistently
  if (breadcrumbs.length <= 1) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage className="flex items-center gap-1">
              <Home className="h-4 w-4" />
              Dashboard
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          
          return (
            <React.Fragment key={crumb.href}>
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage className="flex items-center gap-1">
                    {crumb.isHome && <Home className="h-4 w-4" />}
                    {crumb.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.href} className="flex items-center gap-1">
                      {crumb.isHome && <Home className="h-4 w-4" />}
                      {crumb.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}